import _ from 'lodash';
import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';

import Button, { ButtonTypes } from 'components/common/button';
import ChevronDownIcon from 'scripts/presentation/components/lib/icons/chevron_down_icon';
import ChevronUpIcon from 'scripts/presentation/components/lib/icons/chevron_up_icon';
import connect from 'components/lib/connect';
import PopoverMenu, { PopoverMenuItem, usePopoverMenu } from 'components/common/menu';
import TrackEvent from 'actions/analytics/track_event';
import { useExecuteAction } from 'components/hooks/connect_hooks';

export function DefaultEndpointSelect({ endpoints, label, onChange, type, value }) {
  const { targetRef, setTargetRef, isOpen, onClose, onToggle } = usePopoverMenu();
  const trackDefaultEndpointChange = useTrackDefaultEndpointChange();
  const decoratedOnChange = useCallback(newValue => {
    onClose();
    onChange(type, newValue);
    trackDefaultEndpointChange(type, newValue);
  });

  if (!endpoints.length) {
    return null;
  }

  const selectedEndpoint = _.find(endpoints, e => e.id === value);
  const displayedValue = selectedEndpoint ? selectedEndpoint.address : 'None';
  const displayedEndpoints = _.filter(endpoints, e => e.id !== value);

  return (
    <EndpointSelect>
      <Header>{label}</Header>
      <OpenerButton onClick={onToggle} ref={setTargetRef}>
        {displayedValue}
        {isOpen ? <UpIcon /> : <DownIcon />}
      </OpenerButton>
      <PopoverMenu
        data-aid={`defaultEndpointSelect-${type}`}
        isOpen={isOpen}
        onClose={onClose}
        position="bottom"
        targetPosition="start"
        targetRef={targetRef}
      >
        <ScrollingMenu>
          {selectedEndpoint ? <EmptyEndpointItem onChange={decoratedOnChange} onClose={onClose} /> : null}
          {_.map(displayedEndpoints, endpoint => (
            <EndpointItem endpoint={endpoint} key={endpoint.id} onChange={decoratedOnChange} />
          ))}
        </ScrollingMenu>
      </PopoverMenu>
    </EndpointSelect>
  );
}

function EndpointItem({ endpoint, onChange }) {
  const onClick = useCallback(() => {
    onChange(endpoint.id);
  }, [onChange, endpoint.id]);
  return <PopoverMenuItem onClick={onClick}>{endpoint.address}</PopoverMenuItem>;
}

function EmptyEndpointItem({ onChange }) {
  const onClick = useCallback(() => {
    onChange(undefined);
  }, [onChange]);
  return <PopoverMenuItem onClick={onClick}>None</PopoverMenuItem>;
}

const EndpointSelect = styled.div`
  border-bottom: 1px solid ${p => p.theme.colors.gray400};
  padding: ${p => p.theme.spacing.large};
`;
const Header = styled.div`
  color: ${p => p.theme.colors.gray600};
  text-transform: uppercase;
`;

const OpenerButton = styled(Button).attrs({ buttonType: ButtonTypes.SECONDARY })`
  align-items: center;
  display: flex;
  margin-top: ${p => p.theme.spacing.medium};
`;

const chevronCommon = css`
  stroke: ${p => p.theme.colors.gray600};
  fill: ${p => p.theme.colors.gray600};
  height: 16px;
  margin-left: ${p => p.theme.spacing.medium};
  width: 13px;
  line-height: 0;
`;

const DownIcon = styled(ChevronDownIcon)`
  ${chevronCommon};
`;

const UpIcon = styled(ChevronUpIcon)`
  ${chevronCommon};
`;

const ScrollingMenu = styled.div`
  max-height: 275px;
  overflow: auto;
`;

const DefaultEndpointSelectContainer = connect(mapStateToProps)(DefaultEndpointSelect);

function mapStateToProps({ getProvider }, { type }) {
  const channelConfiguration = getProvider('channelConfiguration').get();
  let endpoints = channelConfiguration ? channelConfiguration.getEndpointsOfType(type) : [];
  endpoints = _.sortBy(endpoints, endpoint => endpoint.address.toLowerCase());

  return {
    endpoints,
  };
}

function useTrackDefaultEndpointChange() {
  const executeAction = useExecuteAction();
  return useCallback((type, value) => {
    executeAction(TrackEvent, {
      event: 'Admin Inboxes - Change Default Endpoint',
      props: { type, value },
    });
  }, []);
}

export default DefaultEndpointSelectContainer;



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/admin/routing_groups/default_endpoint_select.jsx